import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Form, InputGroup, ProgressBar, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import CustomerDropdown from './CustomerDropdown';
import { ApiError, saveDepositAllocations } from './customerCenterApi';
import { displayMoney } from './money';
import type { BranchId, DepositDetail } from './types';

export interface SplitEditorProps {
  branchId: BranchId;
  detail: DepositDetail;
  onSaved: (d: DepositDetail) => void;
  onCancel: () => void;
}

interface SplitRow {
  key: string;
  customer_id: number | null;
  customer_name: string | null;
  active: boolean | null;
  amount: string;
}

const MONEY_INPUT_RE = /^\d+(\.\d{1,2})?$/;

type SplitT = (key: string, options?: Record<string, string | number>) => string;

let rowSequence = 0;
function nextRowKey(): string {
  rowSequence += 1;
  return `split-row-${rowSequence}`;
}

/** Plain non-negative decimal to integer cents; null when invalid. */
function toCents(value: string): number | null {
  if (!MONEY_INPUT_RE.test(value)) return null;
  const [whole, frac = ''] = value.split('.');
  return parseInt(whole, 10) * 100 + parseInt(frac.padEnd(2, '0'), 10);
}

/** Integer cents to a fixed two-decimal string (for money.ts formatting). */
function centsToDecimal(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/** Sum of valid row amounts in cents; invalid amounts count as zero (reported by validate). */
function assignedCents(rows: SplitRow[]): number {
  return rows.reduce((sum, row) => sum + (toCents(row.amount.trim()) ?? 0), 0);
}

function rowMessages(rows: SplitRow[], showRow: (row: SplitRow) => boolean, t: SplitT): string[] {
  const messages: string[] = [];
  if (rows.some((row) => showRow(row) && row.customer_id === null)) {
    messages.push(t('splitMsgChoose'));
  }
  if (rows.some((row) => {
    if (!showRow(row)) return false;
    const cents = toCents(row.amount.trim());
    return cents === null || cents <= 0;
  })) {
    messages.push(t('splitMsgAmount'));
  }
  return messages;
}
function liveMessages(rows: SplitRow[], depositCents: number | null, t: SplitT): string[] {
  const messages: string[] = [];
  const ids = rows.map((row) => row.customer_id).filter((id): id is number => id !== null);
  if (new Set(ids).size !== ids.length) {
    messages.push(t('splitMsgDuplicate'));
  }
  if (depositCents !== null && assignedCents(rows) > depositCents) {
    messages.push(t('splitMsgOver'));
  }
  return messages;
}
function saveErrorMessage(err: unknown, t: SplitT): string {
  if (err instanceof ApiError && err.status === 403) {
    return t('splitMsgDenied');
  }
  const messageText = err instanceof ApiError ? err.messageText : null;
  const message = err instanceof Error ? err.message : '';
  return messageText || message || t('splitMsgSaveFallback');
}

export default function SplitEditor({ branchId, detail, onSaved, onCancel }: SplitEditorProps) {
  const { t } = useTranslation('scenes.customerCenter');
  const [rows, setRows] = useState<SplitRow[]>(() => (detail.allocations.length > 0
    ? detail.allocations.map((allocation) => ({
      key: nextRowKey(),
      customer_id: Number(allocation.customer_id),
      customer_name: allocation.customer_name,
      active: allocation.active,
      amount: allocation.amount ?? '',
    }))
    : [{
      key: nextRowKey(),
      customer_id: null,
      customer_name: null,
      active: null,
      amount: detail.deposit_amount ?? '',
    }]),
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [touchedKeys, setTouchedKeys] = useState<Set<string>>(new Set());
  const [saveAttempted, setSaveAttempted] = useState(false);

  const depositCents = useMemo(() => toCents(detail.deposit_amount ?? ''), [detail.deposit_amount]);
  const assigned = useMemo(() => assignedCents(rows.filter((row) => row.customer_id !== null)), [rows]);
  const remaining = depositCents === null ? null : centsToDecimal(depositCents - assigned);
  const remainingCents = depositCents === null ? null : depositCents - assigned;
  const liveErrors = useMemo(() => liveMessages(rows, depositCents, t), [rows, depositCents, t]);
  const errors = useMemo(
    () => [...rowMessages(rows, () => true, t), ...liveMessages(rows, depositCents, t)],
    [rows, depositCents, t],
  );
  const shownErrors = useMemo(
    () => [
      ...rowMessages(rows, (row) => saveAttempted || touchedKeys.has(row.key), t),
      ...liveMessages(rows, depositCents, t),
    ],
    [rows, depositCents, saveAttempted, touchedKeys, t],
  );
  const excludeIds = rows.flatMap((row) => (row.customer_id === null ? [] : [row.customer_id]));
  const over = remainingCents !== null && remainingCents < 0;
  const progress = depositCents ? Math.min(100, Math.round((assigned / depositCents) * 100)) : 0;
  const progressVariant = over ? 'danger' : remainingCents === 0 ? 'success' : 'primary';

  const touch = (key: string) => setTouchedKeys((prev) => new Set(prev).add(key));

  const handleSave = () => {
    setSaveAttempted(true);
    if (saving || errors.length > 0) return;
    setSaving(true);
    setSaveError(null);
    saveDepositAllocations(
      branchId,
      detail.id,
      rows.map((row) => ({ customer_id: row.customer_id!, amount: row.amount.trim() })),
    )
      .then(onSaved)
      .catch((err: unknown) => {
        setSaveError(saveErrorMessage(err, t));
        setSaving(false);
      });
  };

  return (
    <div className="split-editor">
      <div className="split-editor__summary">
        <div>
          <div className="text-muted small">{t('detailDepositTotal')}</div>
          <strong>{displayMoney(detail.deposit_amount)}</strong>
        </div>
        <div>
          <div className="text-muted small">{t('detailAssigned')}</div>
          <strong>{displayMoney(centsToDecimal(assigned))}</strong>
        </div>
        <div>
          <div className="text-muted small">{t('detailLeft')}</div>
          <strong className={over ? 'text-danger' : undefined}>
            {displayMoney(remaining)}
            {over && remainingCents !== null && (
              <span className="small fw-normal"> {t('detailOverBy', { amount: displayMoney(centsToDecimal(-remainingCents)) })}</span>
            )}
          </strong>
        </div>
      </div>
      <ProgressBar now={progress} variant={progressVariant} className="split-editor__progress" aria-label={t('detailAssigned')} />
      {remainingCents !== null && remainingCents > 0 && rows.length > 0 && rows.every((row) => row.customer_id !== null) && (
        <div className="text-muted small mt-2">{t('detailSaveLeaves', { amount: displayMoney(remaining) })}</div>
      )}

      {/* fixme: placeholder copy */}
      <p className="split-editor__help">{t('splitHelp')}</p>

      <div className="split-editor__row split-editor__row--head" aria-hidden="true">
        <span>{t('splitColCustomer')}</span>
        <span>{t('splitColAmount')}</span>
        <span />
      </div>
      {rows.length === 0 && <div className="split-editor__empty">{t('splitNoSplits')}</div>}
      {rows.map((row, index) => (
        <div key={row.key} className="split-editor__row">
          <div className="split-editor__customer">
            <div className="flex-grow-1">
              <CustomerDropdown
                branchId={branchId}
                excludeIds={excludeIds.filter((id) => id !== row.customer_id)}
                disabled={saving}
                suggestedCustomer={row.customer_id === null ? detail.suggested_customer : null}
                customer={row.customer_id === null
                  ? null
                  : {
                    id: row.customer_id,
                    name: row.customer_name ?? t('splitUnknownCustomer'),
                    active: row.active ?? false,
                  }}
                ariaLabel={t('splitCustomerFor', { line: index + 1 })}
                onPick={(customer) => {
                  touch(row.key);
                  setRows((prev) => prev.map((r) => (r.key === row.key
                    ? {
                      ...r,
                      customer_id: customer ? Number(customer.id) : null,
                      customer_name: customer?.name ?? null,
                      active: customer?.active ?? null,
                    }
                    : r)));
                }}
              />
            </div>
            {row.active === false && <Badge bg="light" text="dark" className="border">{t('splitInactive')}</Badge>}
          </div>
          <InputGroup>
            <InputGroup.Text>$</InputGroup.Text>
            <Form.Control
              type="text"
              inputMode="decimal"
              value={row.amount}
              placeholder={t('splitAmountPlaceholder')}
              aria-label={t('splitAmountFor', { name: row.customer_name ?? t('splitUnknownCustomer') })}
              disabled={saving}
              onChange={(e) => {
                touch(row.key);
                setRows((prev) => prev.map((r) => (r.key === row.key ? { ...r, amount: e.target.value } : r)));
              }}
              onBlur={() => touch(row.key)}
            />
          </InputGroup>
          <div className="split-editor__row-actions">
            {remainingCents !== null && remainingCents > 0 && (
              <Button
                variant="link"
                size="sm"
                className="p-0"
                disabled={saving}
                onClick={() => {
                  touch(row.key);
                  setRows((prev) => prev.map((r) => (r.key === row.key
                    ? { ...r, amount: centsToDecimal((toCents(r.amount.trim()) ?? 0) + remainingCents) }
                    : r)));
                }}
              >
                {t('splitFillRemaining')}
              </Button>
            )}
            <Button
              variant="link"
              size="sm"
              className="p-0 text-danger"
              disabled={saving}
              onClick={() => setRows((prev) => prev.filter((r) => r.key !== row.key))}
            >
              {t('splitRemove')}
            </Button>
          </div>
        </div>
      ))}

      <div className="d-flex flex-wrap align-items-center gap-3 mt-2">
        <Button
          variant="outline-primary"
          size="sm"
          disabled={saving}
          onClick={() => setRows((prev) => [...prev, { key: nextRowKey(), customer_id: null, customer_name: null, active: null, amount: '' }])}
        >
          {/* fixme: placeholder copy */}
          + {t('splitAdd')}
        </Button>
        {rows.length === 1 && <span className="text-muted small">{t('splitHintSingle')}</span>}
        {rows.length > 1 && !confirmClear && (
          <Button variant="link" size="sm" className="p-0 text-danger" disabled={saving} onClick={() => setConfirmClear(true)}>
            {t('splitClear')}
          </Button>
        )}
      </div>

      {confirmClear && (
        <div className="d-flex align-items-center gap-2 mt-2">
          <span>{t('splitRemoveAll')}</span>
          <Button
            variant="danger"
            size="sm"
            disabled={saving}
            onClick={() => {
              setRows([]);
              setConfirmClear(false);
            }}
          >
            {t('splitRemove')}
          </Button>
          <Button variant="outline-secondary" size="sm" disabled={saving} onClick={() => setConfirmClear(false)}>
            {t('splitKeep')}
          </Button>
        </div>
      )}

      {shownErrors.length > 0 && (
        <Alert variant="warning" className="py-2 mt-3 mb-0" id="split-errors">
          <ul className="mb-0 ps-3">
            {shownErrors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </Alert>
      )}

      {saveError && <Alert variant="danger" className="mt-3 mb-0">{saveError}</Alert>}

      <div className="split-editor__footer">
        <Button variant="outline-secondary" disabled={saving} onClick={onCancel}>
          {t('splitCancel')}
        </Button>
        <Button
          variant="primary"
          disabled={saving || liveErrors.length > 0}
          onClick={handleSave}
          aria-describedby={shownErrors.length > 0 ? 'split-errors' : undefined}
        >
          {saving && (
            <Spinner animation="border" size="sm" className="me-2" role="status">
              <span className="visually-hidden">{t('splitSaving')}</span>
            </Spinner>
          )}
          {t('splitSave')}
        </Button>
      </div>
    </div>
  );
}
